/* eslint-disable react-refresh/only-export-components */

import { createContext, useContext, useState, ReactNode, useMemo } from 'react';
import { LatLng } from 'leaflet';
import { Incident } from '@/types/incidents';

interface CircleSelectionContextType {
  isCircleSelectionMode: boolean;
  setCircleSelectionMode: (mode: boolean) => void;
  circleCenter: LatLng | null;
  setCircleCenter: (center: LatLng | null) => void;
  circleRadius: number;
  setCircleRadius: (radius: number) => void;
  selectedAlerts: Incident[];
  setSelectedAlerts: (alerts: Incident[]) => void;
  isGroupingPanelOpen: boolean;
  setIsGroupingPanelOpen: (open: boolean) => void;
}

const CircleSelectionContext = createContext<CircleSelectionContextType | undefined>(undefined);

export const CircleSelectionProvider = ({ children }: { children: ReactNode }) => {
  const [isCircleSelectionMode, setCircleSelectionMode] = useState(false);
  const [circleCenter, setCircleCenter] = useState<LatLng | null>(null);
  const [circleRadius, setCircleRadius] = useState(0);
  const [selectedAlerts, setSelectedAlerts] = useState<Incident[]>([]);
  const [isGroupingPanelOpen, setIsGroupingPanelOpen] = useState(false);

  const value = useMemo(() => ({
    isCircleSelectionMode,
    setCircleSelectionMode,
    circleCenter,
    setCircleCenter,
    circleRadius,
    setCircleRadius,
    selectedAlerts,
    setSelectedAlerts,
    isGroupingPanelOpen,
    setIsGroupingPanelOpen,
  }), [isCircleSelectionMode, circleCenter, circleRadius, selectedAlerts, isGroupingPanelOpen]);

  return (
    <CircleSelectionContext.Provider value={value}>
      {children}
    </CircleSelectionContext.Provider>
  );
};

export const useCircleSelection = () => {
  const context = useContext(CircleSelectionContext);
  if (context === undefined) {
    throw new Error('useCircleSelection must be used within a CircleSelectionProvider');
  }
  return context;
};
